import {
  Box,
  Button,
  Field,
  Flex,
  Input,
  Modal,
  Select,
  toast,
} from '@workwhile/ui'
import {
  AdminRequirementRow,
  GetAllRequirementsRequest,
} from 'api/company/position/requirement/getAllPositionRequirements'
import { Plus } from 'lucide-react'

import { useAddRequirementToPositionMutation } from 'queries/company/useAddRequirementToPositionMutation'
import { useAllPositionRequirementsQuery } from 'queries/company/useAllPositionRequirements'
import { useRemoveRequirementFromPositionMutation } from 'queries/company/useRemoveRequirementFromPositionMutation'
import { useRequirementDataForPositionQuery } from 'queries/company/useRequirementDataForPositionQuery'
import React, { useCallback, useMemo, useState } from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import Loading from '../Shared/loading'
import CreateRequirementForPosition from './CreateRequirementForPosition'
import { useAssessmentLevelRequirements } from './hooks/useAssessmentLevelRequirements'

const boolFormatter = (cell, row) => {
  return cell ? <span>✅</span> : <span>❌</span>
}

const mustHaveColumn = {
  dataField: 'mustHave',
  text: 'Must Have?',
  editable: false,
  formatter: boolFormatter,
}

const requirementColumns = [
  {
    dataField: 'id',
    text: 'Requirement ID',
    editable: false,
  },
  {
    dataField: 'name',
    text: 'Requirement Name',
    editable: false,
  },
  {
    dataField: 'category',
    text: 'Category',
    editable: false,
  },
  {
    dataField: 'verifiable',
    text: 'Verifiable?',
    editable: false,
    formatter: boolFormatter,
  },
  {
    dataField: 'isWorkerVisible',
    text: 'Visible to worker',
    editable: false,
    formatter: boolFormatter,
  },
  {
    dataField: 'verificationType',
    text: 'Verification Type',
    editable: false,
  },
]

interface Props {
  positionId: number | null
}

/**
 * Search for requirements by name, id, position id, or skill level.
 */

const RequirementsForPosition = ({ positionId }: Props) => {
  const [displayCount, setDisplayCount] = useState(10)
  const [searchParams, setSearchParams] = useState<
    GetAllRequirementsRequest & {
      assessmentLevel?: string
      positionId?: number
    }
  >({
    requirementId: undefined,
    nameSubstring: undefined,
    positionId: undefined,
    assessmentLevel: undefined,
  })
  const [showCreateRequirementModal, setShowCreateRequirementModal] =
    useState(false)

  /** Get current position requirements */
  const {
    data: { positionRequirementData: currentPositionRequirements = [] } = {
      positionRequirementData: [],
    },
    isPending: isPendingCurrentPositionRequirements,
    refetch: refetchCurrentPositionRequirements,
  } = useRequirementDataForPositionQuery(positionId)

  /** If there is a position ID filter, fetch the requirements for that position id */
  const {
    data: { positionRequirementData: searchPositionRequirements } = {
      positionRequirementData: [],
    },
    isFetching: isFetchingOtherPositionRequirements,
  } = useRequirementDataForPositionQuery(searchParams.positionId || null)

  /** Fetch all requirements with name/requirement id search */
  const {
    data: nameOrRequirementIdRequirements = [],
    isFetching: isFetchingAllPositionRequirements,
  } = useAllPositionRequirementsQuery({
    nameSubstring: searchParams.nameSubstring,
    requirementId: searchParams.requirementId,
  })

  /** Fetch all assessment level requirements */
  const {
    data: assessmentLevelRequirements = {},
    isPending: isLoadingAssessmentRequirements,
  } = useAssessmentLevelRequirements()
  const assessmentLevelOptions = useMemo(() => {
    return [
      { label: '--', value: null },
      ...Object.keys(assessmentLevelRequirements).map(
        (assessmentLevelOption) => ({
          label: assessmentLevelOption,
          value: assessmentLevelOption,
        })
      ),
    ]
  }, [assessmentLevelRequirements])

  /** Filter out current position requirements */
  const isCurrentRequirement = useCallback(
    (requirement: AdminRequirementRow) => {
      return (currentPositionRequirements ?? []).some(
        (currentRequirement) =>
          Number(currentRequirement.id) === Number(requirement.id)
      )
    },
    [currentPositionRequirements]
  )

  /** Filter out assessment level requirements that are higher level than the selected assessment level */
  const isMinimumAcceptableLevel = useCallback(
    (requirement: AdminRequirementRow) => {
      if (!searchParams.assessmentLevel) return true

      // GraphQL may return requirement.id as string; assessmentLevelRequirements use numbers.
      return assessmentLevelRequirements[searchParams.assessmentLevel]?.some(
        (workerRequirementId) =>
          Number(workerRequirementId) === Number(requirement.id)
      )
    },
    [assessmentLevelRequirements, searchParams.assessmentLevel]
  )

  /** Apply all filters to search results */
  const searchResults = useMemo(() => {
    const _requirements: AdminRequirementRow[] = searchParams.positionId
      ? searchPositionRequirements
      : nameOrRequirementIdRequirements

    return _requirements.filter((req) =>
      [!isCurrentRequirement(req), isMinimumAcceptableLevel(req)].every(Boolean)
    )
  }, [
    searchParams.positionId,
    searchPositionRequirements,
    nameOrRequirementIdRequirements,
    isCurrentRequirement,
    isMinimumAcceptableLevel,
  ])

  /** Add and remove requirements from position */
  const {
    mutate: addRequirementToPosition,
    isPending: isAddRequirementPending,
  } = useAddRequirementToPositionMutation(positionId)
  const {
    mutate: removeRequirementFromPosition,
    isPending: isRemoveRequirementPending,
  } = useRemoveRequirementFromPositionMutation(positionId)

  const requirementIdInput = (
    <Field label="Requirement ID">
      <Input
        block
        name="requirementId"
        value={searchParams.requirementId ?? ''}
        onChange={(e) =>
          setSearchParams((state) => ({
            ...state,
            requirementId: e.target.value ? Number(e.target.value) : undefined,
          }))
        }
        type="text"
        placeholder="Requirement ID"
      />
    </Field>
  )

  const positionIdInput = (
    <Field label="Position ID">
      <Input
        block
        name="positionId"
        value={searchParams.positionId ?? ''}
        onChange={(e) =>
          setSearchParams((state) => ({
            ...state,
            positionId: e.target.value ? Number(e.target.value) : undefined,
          }))
        }
        type="text"
        placeholder="Position ID"
      />
    </Field>
  )

  const requirementNameInput = (
    <Field label="Requirement name">
      <Input
        block
        name="requirementName"
        value={searchParams.nameSubstring ?? ''}
        onChange={(e) => {
          setDisplayCount(10)
          setSearchParams((state) => ({
            ...state,
            nameSubstring: e.target.value ? e.target.value : '',
          }))
        }}
        type="text"
        placeholder="Search by name"
      />
    </Field>
  )

  const assessmentLevelInput = (
    <Field label="Minimum Acceptable Level">
      <Select
        placeholder="Minimum Acceptable Level"
        name="assessmentLevel"
        value={
          assessmentLevelOptions.find(
            (option) => option.value === searchParams.assessmentLevel
          ) ?? null
        }
        onChange={(selection) =>
          setSearchParams((state) => ({
            ...state,
            assessmentLevel: selection?.value,
          }))
        }
        options={assessmentLevelOptions}
        isClearable
        isSearchable
      />
    </Field>
  )

  function createRequirementButtonColumn(actionType) {
    if (!currentPositionRequirements.length) return null

    return {
      dataField: 'button',
      text: 'Action',
      editable: false,
      formatter: (cell, row) => {
        const buttonText = actionType
        const isDisabled =
          actionType === 'add'
            ? isAddRequirementPending
            : isRemoveRequirementPending
        return (
          <button
            type="button"
            disabled={isDisabled}
            onClick={() => onRequirementRowClicked(row, actionType)}
          >
            {buttonText}
          </button>
        )
      },
      headerStyle: { width: '10%' },
    }
  }

  function getRequirementColumns(actionType) {
    const buttonCol = createRequirementButtonColumn(actionType)
    let columns = requirementColumns
    if (buttonCol) {
      columns = [buttonCol, ...columns]
    }
    if (actionType === 'remove') {
      columns = [...columns, mustHaveColumn]
    }
    return columns
  }

  function onRequirementRowClicked(row, actionType) {
    if (actionType === 'add') {
      addRequirementToPosition(
        { requirementId: row.id },
        {
          onSuccess: () => {
            toast.success('Requirement added ✅')
            void refetchCurrentPositionRequirements()
          },
          onError: (error) => {
            toast.error('Error adding requirement')
            console.error(error)
          },
        }
      )
    } else if (actionType === 'remove') {
      removeRequirementFromPosition(
        { requirementId: row.id },
        {
          onSuccess: () => {
            toast.success('Requirement removed 🗑️')
            void refetchCurrentPositionRequirements()
          },
          onError: (error) => {
            toast.error('Error removing requirement')
            console.error(error)
          },
        }
      )
    } else {
      console.error('Invalid action type:', actionType)
    }
  }

  const isLoadingSearchResults =
    isFetchingOtherPositionRequirements ||
    isLoadingAssessmentRequirements ||
    isFetchingAllPositionRequirements

  return (
    <Box>
      <h5>Current Requirements</h5>
      {isPendingCurrentPositionRequirements ? (
        <Loading />
      ) : (
        <BootstrapTable
          keyField="id"
          data={currentPositionRequirements}
          columns={getRequirementColumns('remove')}
          striped
          rowStyle={{ fontSize: 14 }}
        />
      )}

      <h5>Search for Existing Requirement</h5>
      <Box mb={2}>
        Search for requirement by ID, substring, or see all requirements on
        another position. Requirements that are already on the position are
        filtered out.
      </Box>
      <Flex flexDirection="row" gap={3} width="100%">
        {requirementNameInput}
        {requirementIdInput}
        {positionIdInput}
        {assessmentLevelInput}
      </Flex>

      {isLoadingSearchResults ? (
        <Loading />
      ) : (
        <Flex flexDirection="column" gap={2}>
          <BootstrapTable
            keyField="id"
            data={searchResults?.slice(0, displayCount)}
            columns={getRequirementColumns('add')}
            striped
            rowStyle={{ fontSize: 14 }}
          />
          <Flex gap={2} flexDirection="row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDisplayCount(displayCount + 10)}
              disabled={searchResults?.length <= displayCount}
            >
              Show More
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowCreateRequirementModal(true)
              }}
            >
              <Plus />
              Create New Requirement
            </Button>
          </Flex>
        </Flex>
      )}

      {showCreateRequirementModal ? (
        <Modal
          title="Create New Requirement"
          open
          onClose={() => setShowCreateRequirementModal(false)}
        >
          <CreateRequirementForPosition
            positionId={positionId}
            successCallback={() => {
              void refetchCurrentPositionRequirements()
              setShowCreateRequirementModal(false)
            }}
          />
        </Modal>
      ) : null}
    </Box>
  )
}

export default RequirementsForPosition
